import { useErrorHandlingSlice } from 'app/error/slice';
import { useState } from 'react';
import { useDispatch } from 'react-redux';
import useApi from 'services/mavapi/useApi';
import {
    RegistrationPageImageService,
    RegistrationPageImageResponse,
} from 'services/openapi';

const useRegistrationPageImageApi = () => {
    const { actions } = useErrorHandlingSlice();
    const dispatch = useDispatch();
    const [, setError] = useState<unknown | null>();
    const { error: apiError, handleRequest } = useApi();
    const [loading, setLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);

    const {
        registrationPageImageGetAllRegistrationPageImages,
        registrationPageImageGetRegistrationPageImageByType,
        registrationPageImageUploadOrUpdateRegistrationPageImage,
        registrationPageImageDeleteRegistrationPageImage,
    } = RegistrationPageImageService;

    // States
    const [allImages, setAllImages] = useState<RegistrationPageImageResponse[]>();
    const [imageByType, setImageByType] = useState<RegistrationPageImageResponse>();

    const getAllRegistrationPageImages = () => {
        setLoading(true);
        handleRequest(registrationPageImageGetAllRegistrationPageImages())
            .then((res) => {
                setAllImages(res);
                setLoading(false);
            })
            .catch(() => {
                setError(apiError);
                setLoading(false);
            });
    };

    const getRegistrationPageImageByType = (type: number) => {
        setLoading(true);
        handleRequest(registrationPageImageGetRegistrationPageImageByType(type))
            .then((res) => {
                setImageByType(res);
                setLoading(false);
            })
            .catch(() => {
                setError(apiError);
                setLoading(false);
            });
    };

    const uploadOrUpdateRegistrationPageImage = (
        type: number,
        file: Blob
    ): Promise<string> => {
        setIsUploading(true);
        return new Promise((resolve, reject) => {
            handleRequest(
                registrationPageImageUploadOrUpdateRegistrationPageImage(type, {
                    file,
                })
            )
                .then((res) => {
                    dispatch(
                        actions.dispatchToast({
                            errorTxt: 'Registration Page Image Uploaded Successfully',
                            show: true,
                            errorType: 'success',
                        })
                    );
                    // Refresh the image list after successful upload
                    getAllRegistrationPageImages();
                    resolve(res);
                    setIsUploading(false);
                })
                .catch(() => {
                    reject(apiError);
                    setIsUploading(false);
                });
        });
    };

    const deleteRegistrationPageImage = (type: number): Promise<void> => {
        setIsDeleting(true);
        return new Promise((resolve, reject) => {
            handleRequest(registrationPageImageDeleteRegistrationPageImage(type))
                .then(() => {
                    dispatch(
                        actions.dispatchToast({
                            errorTxt: 'Registration Page Image Deleted Successfully',
                            show: true,
                            errorType: 'success',
                        })
                    );
                    // Refresh the image list after successful delete
                    getAllRegistrationPageImages();
                    resolve();
                    setIsDeleting(false);
                })
                .catch(() => {
                    reject(apiError);
                    setIsDeleting(false);
                });
        });
    };

    return {
        loading,
        isUploading,
        isDeleting,
        allImages,
        imageByType,
        getAllRegistrationPageImages,
        getRegistrationPageImageByType,
        uploadOrUpdateRegistrationPageImage,
        deleteRegistrationPageImage,
    };
};

export default useRegistrationPageImageApi;